import { isPlatformBrowser } from '@angular/common';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Shared motion primitives for the ResilienceSoft design system.
 *
 * Mirrors the timings already approved on the Home / About pages:
 * duration 0.5–0.8s, `power3.out` / `power4.out`, small y offsets and short
 * staggers. Everything is SSR-safe and honours `prefers-reduced-motion`.
 */

let pluginsRegistered = false;

/** Registers ScrollTrigger exactly once per app instance. */
export function registerRsPlugins(): void {
  if (pluginsRegistered) return;
  gsap.registerPlugin(ScrollTrigger);
  pluginsRegistered = true;
}

export type RsRevealKind = 'up' | 'down' | 'left' | 'right' | 'scale' | 'blur';

export interface RsRevealOptions {
  /** Direction / character of the reveal. */
  kind?: RsRevealKind;
  /** Seconds between siblings when a NodeList is passed. */
  stagger?: number;
  /** Seconds. */
  duration?: number;
  /** Seconds. */
  delay?: number;
  /** ScrollTrigger `start`. Ignored when `immediate` is true. */
  start?: string;
  /** Play on load instead of on scroll (hero content). */
  immediate?: boolean;
  /** Element that triggers the reveal; defaults to the first target. */
  trigger?: Element;
}

const FROM: Record<RsRevealKind, gsap.TweenVars> = {
  up: { y: 26 },
  down: { y: -26 },
  left: { x: -32 },
  right: { x: 32 },
  scale: { scale: 0.94 },
  blur: { filter: 'blur(10px)' },
};

/** The value each animatable entrance property must land on. */
const REST: Record<string, number | string> = {
  opacity: 1,
  autoAlpha: 1,
  x: 0,
  y: 0,
  xPercent: 0,
  yPercent: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  rotate: 0,
  rotation: 0,
  skewX: 0,
  skewY: 0,
  filter: 'none',
};

/**
 * Splits a `from()`-style vars object into an explicit `[fromVars, toVars]`
 * pair for `fromTo()`.
 *
 * `gsap.from()` resolves its destination by reading the DOM, which makes it
 * unsafe next to ScrollTrigger: any refresh (a lazy image resizing the page, a
 * client-side route change) invalidates the tween, it re-reads the destination
 * while its own from-state is still applied, records `opacity: 0`, and then
 * animates 0 -> 0 — leaving the section permanently blank. Stating the
 * destination up front makes the tween immune to that.
 *
 * Spread it straight into the call:
 *   tl.fromTo('.x', ...rsFromTo({ opacity: 0, y: 16, duration: 0.5 }), '-=0.2')
 */
export function rsFromTo(vars: gsap.TweenVars): [gsap.TweenVars, gsap.TweenVars] {
  const from: gsap.TweenVars = {};
  const to: gsap.TweenVars = {};
  for (const [key, value] of Object.entries(vars)) {
    if (key in REST) {
      from[key] = value;
      to[key] = REST[key];
    } else {
      // duration, ease, stagger, delay, scrollTrigger, callbacks, clearProps…
      to[key] = value;
    }
  }
  return [from, to];
}

@Injectable({ providedIn: 'root' })
export class RsMotionService {
  readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** True when the visitor asked for reduced motion. */
  get reduced(): boolean {
    return (
      this.isBrowser &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /** True when we may build GSAP timelines at all. */
  get enabled(): boolean {
    return this.isBrowser && !this.reduced;
  }

  /**
   * Marks the document as motion-capable. The `.rs-motion` class is what lets
   * `[data-rs-reveal]` elements start hidden — without it they render normally,
   * so a JS failure can never leave the page blank.
   */
  arm(): void {
    if (!this.enabled) return;
    registerRsPlugins();
    document.documentElement.classList.add('rs-motion');
  }

  /**
   * Builds a scoped GSAP context. Call the returned `revert()` in ngOnDestroy.
   * Returns undefined when motion is disabled, so callers can bail cheaply.
   */
  context(scope: Element, build: (ctx: gsap.Context) => void): gsap.Context | undefined {
    if (!this.enabled) return undefined;
    this.arm();
    return gsap.context(build, scope);
  }

  /**
   * Reveals one or more elements. Adds `.rs-revealed` on completion so the CSS
   * pre-hide rule stops applying and `will-change` is released.
   */
  reveal(
    targets: gsap.TweenTarget,
    {
      kind = 'up',
      stagger = 0.08,
      duration = 0.7,
      delay = 0,
      start = 'top 86%',
      immediate = false,
      trigger,
    }: RsRevealOptions = {}
  ): gsap.core.Tween | undefined {
    const list = gsap.utils.toArray<HTMLElement>(targets);
    if (!list.length) return undefined;

    if (!this.enabled) {
      list.forEach((el) => el.classList.add('rs-revealed'));
      return undefined;
    }

    registerRsPlugins();
    const done = () => list.forEach((el) => el.classList.add('rs-revealed'));

    return gsap.from(list, {
      ...FROM[kind],
      opacity: 0,
      duration,
      delay,
      stagger,
      ease: 'power3.out',
      clearProps: kind === 'blur' ? 'filter,opacity' : 'transform,opacity',
      onStart: done,
      ...(immediate
        ? {}
        : {
            scrollTrigger: {
              trigger: trigger ?? list[0],
              start,
              once: true,
            },
          }),
    });
  }

  /**
   * Counts an element's text up to its numeric value once it scrolls in.
   * Leaves the original text in place when motion is disabled.
   */
  countUp(el: HTMLElement, to: number, duration = 1.6, suffix = ''): void {
    if (!this.enabled) {
      el.textContent = `${to}${suffix}`;
      return;
    }
    registerRsPlugins();
    const state = { v: 0 };
    gsap.to(state, {
      v: to,
      duration,
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 92%', once: true },
      onUpdate: () => {
        el.textContent = `${Math.round(state.v)}${suffix}`;
      },
    });
  }

  /**
   * Parallax-style pointer tracking. Writes `--px` / `--py` in the -1..1 range
   * on `el` so decorative layers can drift via `translate: calc(var(--px)…)`.
   * Returns a teardown function.
   */
  trackPointer(el: HTMLElement): () => void {
    if (!this.enabled) return () => {};

    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--px', `${((e.clientX - r.left) / r.width - 0.5) * 2}`);
      el.style.setProperty('--py', `${((e.clientY - r.top) / r.height - 0.5) * 2}`);
    };
    const onLeave = () => {
      el.style.setProperty('--px', '0');
      el.style.setProperty('--py', '0');
    };

    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }

  /**
   * Toggles `.is-paused` on `el` while it is outside the viewport so its
   * ambient CSS animations stop burning frames. Returns a teardown function.
   */
  pauseOffscreen(el: HTMLElement): () => void {
    if (!this.isBrowser || typeof IntersectionObserver === 'undefined') return () => {};
    const io = new IntersectionObserver(
      (entries) => el.classList.toggle('is-paused', !entries.some((e) => e.isIntersecting)),
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }
}
